import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Trash2, Crown, TrendingUp, Sparkles, Zap, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ScoreRing from "../components/ScoreRing";
import { useSeo } from "../lib/seo";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  useSeo({ title: "Your dashboard", description: "Your saved scans, progress and plan.", path: "/dashboard", noindex: true });

  const load = () => {
    api.get("/scans").then(({ data }) => setScans(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try {
      await api.delete(`/scans/${id}`);
      setScans((s) => s.filter((x) => x.id !== id));
      toast.success("Scan deleted");
    } catch (e) { toast.error("Could not delete"); }
  };

  const cancelPlan = async () => {
    setCanceling(true);
    try {
      await api.post("/billing/cancel");
      await refresh();
      toast.success("Plan cancelled — you're on Free now.");
    } catch (e) { toast.error("Could not cancel"); }
    finally { setCanceling(false); }
  };

  const latest = scans[0];
  const chartData = [...scans].reverse().map((s, i) => ({
    name: `#${i + 1}`,
    score: s.overall_score,
  }));

  return (
    <div className="container py-12">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <span className="label-eyebrow">Dashboard</span>
          <h1 className="mt-2 font-display text-4xl">Welcome back, {user?.name?.split(" ")[0] || "there"}.</h1>
        </div>
        <Link to="/" className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-110" data-testid="dashboard-new-scan">New scan</Link>
      </div>

      {/* Top stats */}
      <div className="mt-8 grid gap-5 lg:grid-cols-4">
        <div className="flex flex-col items-center rounded-xl border border-border bg-card p-6 lg:col-span-1">
          <span className="label-eyebrow mb-3">North Star</span>
          {latest ? <ScoreRing score={latest.overall_score} size={150} label="Latest" testId="dashboard-latest-score" /> : <p className="py-10 text-sm text-muted-foreground">No scans yet</p>}
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:col-span-3">
          <Stat icon={Zap} label="Plan" value={user?.plan?.toUpperCase()} sub={`${user?.used_this_month ?? 0}/${user?.limit} scans used`} />
          <Stat icon={Sparkles} label="Total scans" value={scans.length} sub="saved to your history" />
          <Stat icon={TrendingUp} label="Best score" value={scans.length ? Math.max(...scans.map((s) => s.overall_score)) : "—"} sub="all-time high" />
          <Stat icon={Crown} label="Potential" value={latest?.potential_score ?? "—"} sub="your projected ceiling" />
        </div>
      </div>

      {/* Progress chart */}
      {scans.length > 1 && (
        <div className="mt-8 rounded-xl border border-border bg-card p-6">
          <h2 className="mb-4 font-display text-xl">Progress over time</h2>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                <Line type="monotone" dataKey="score" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={{ r: 4, fill: "hsl(var(--primary))" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* History */}
      <div className="mt-8">
        <h2 className="mb-4 font-display text-xl">Your scans</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : scans.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
            <p className="text-muted-foreground">No scans yet.</p>
            <Link to="/" className="mt-4 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">Run your first scan</Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {scans.map((s) => (
              <div key={s.id} className="group relative overflow-hidden rounded-xl border border-border bg-card" data-testid={`history-${s.id}`}>
                <Link to={`/r/${s.id}`} className="flex items-center gap-4 p-4">
                  {s.image && <img src={s.image} alt="" className="h-16 w-16 rounded-lg object-cover" />}
                  <div>
                    <p className="font-mono text-2xl font-bold text-primary">{s.overall_score}</p>
                    <p className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleDateString()} · {s.face_shape}</p>
                  </div>
                </Link>
                <button onClick={() => remove(s.id)} aria-label="Delete" className="absolute right-2 top-2 rounded-md bg-black/50 p-1.5 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Billing portal */}
      <div className="mt-10 rounded-xl border border-border bg-card p-6">
        <h2 className="flex items-center gap-2 font-display text-xl"><CreditCard className="h-5 w-5 text-primary" /> Billing</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          You're on the <span className="font-semibold text-foreground">{user?.plan?.toUpperCase()}</span> plan.
          {user?.plan_expires_at && ` Renews/expires ${new Date(user.plan_expires_at).toLocaleDateString()}.`}
        </p>
        <div className="mt-4 flex gap-3">
          {user?.plan === "free" ? (
            <Link to="/pricing" className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground" data-testid="dashboard-upgrade">Upgrade plan</Link>
          ) : (
            <>
              <Link to="/pricing" className="rounded-md border border-border px-5 py-2.5 text-sm font-semibold transition hover:border-primary/50">Change plan</Link>
              <button onClick={cancelPlan} disabled={canceling} data-testid="dashboard-cancel" className="rounded-md border border-border px-5 py-2.5 text-sm font-semibold text-destructive transition hover:border-destructive/50 disabled:opacity-60">
                {canceling ? "Cancelling…" : "Cancel plan"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <Icon className="h-5 w-5 text-primary" />
      <p className="label-eyebrow mt-3">{label}</p>
      <p className="mt-1 font-display text-3xl">{value}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}
